import { QUESTION_HIDDEN } from '@assessments/shared'

/**
 * A non-2xx response from the assessments API, carrying the status so callers can
 * branch on it (401 ⇒ the platform session ended, 409 ⇒ a domain conflict) instead
 * of parsing it back out of a message string.
 */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/** True when a write was refused because the assessment has been submitted. */
export function isAssessmentSubmitted(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    err.status === 409 &&
    (err.body as { code?: unknown } | undefined)?.code === 'ASSESSMENT_SUBMITTED'
  )
}

/** True when skip logic hides the question the write targeted. */
export function isQuestionHidden(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    err.status === 409 &&
    (err.body as { code?: unknown } | undefined)?.code === QUESTION_HIDDEN
  )
}

/** True when the platform session is gone and the app must stop acting on its behalf. */
export function isSessionExpired(err: unknown): boolean {
  return err instanceof ApiError && err.status === 401
}

/**
 * Throw an `ApiError` unless the response succeeded.
 *
 * Only for the assessments API — `apiFetch` also serves the platform navigation and
 * logout origins, where a 401 says nothing about this app's session.
 *
 * TODO: the read paths still throw plain Errors; adopt this there too so there is one
 * error model across the feature api.ts modules.
 */
export async function assertOk(res: Response, verb: string): Promise<void> {
  if (res.ok) return
  const body = await res.json().catch(() => undefined)
  throw new ApiError(res.status, `Failed to ${verb} (${res.status})`, body)
}
